// src/context/TranslationContext.tsx
import React, {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useState,
} from "react";
import { getTranslations } from "../api/api";

import enBundle from "../locales/en.json";
import deBundle from "../locales/de.json";
import frBundle from "../locales/fr.json";
import itBundle from "../locales/it.json";

interface Translations {
    [key: string]: string;
}

interface TranslationContextProps {
    lang: string;
    setLang: (newLang: string) => void;
    translations: Translations;
    t: (key: string) => string;
    isLoading: boolean;
    error: string | null;
}

const TranslationContext = createContext<TranslationContextProps>({
    lang: "EN",
    setLang: () => { },
    translations: enBundle,
    t: (key: string) => key,
    isLoading: false,
    error: null,
});

function getLocalBundle(lang: string): Translations {
    switch (lang) {
        case "DE":
            return deBundle;
        case "FR":
            return frBundle;
        case "IT":
            return itBundle;
        default:
            return enBundle;
    }
}

const STORAGE_KEY_PREFIX = "translations_";
const TRANSLATION_TTL_MS = 30 * 60 * 1000; // 30 minutes

interface StoredTranslations {
    data: Translations;
    fetchedAt: number;
}

function getStorageKey(lang: string) {
    return `${STORAGE_KEY_PREFIX}${lang.toLowerCase()}`;
}

interface TranslationProviderProps {
    children: ReactNode;
    initialLang?: string;
}

export function TranslationProvider({
    children,
    initialLang,
}: TranslationProviderProps) {
    const [lang, setLang] = useState<string>(() => {
        if (initialLang) return initialLang;
        return window.localStorage.getItem("appLang") || "DE";
    });

    const [translations, setTranslations] = useState<Translations>(
        () => getLocalBundle(lang)
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    // Persist language immediately when user selects it
    const updateLang = (newLang: string) => {
        setLang(newLang);
        window.localStorage.setItem("appLang", newLang);
    };

    useEffect(() => {
        const storageKey = getStorageKey(lang);
        const apiLang = lang.toLowerCase();

        let stored: StoredTranslations | null = null;
        let initialTranslations: Translations | null = null;

        const rawStored = window.localStorage.getItem(storageKey);
        if (rawStored) {
            try {
                stored = JSON.parse(rawStored) as StoredTranslations;
                initialTranslations = stored.data;
            } catch {
                // ignore parse errors
            }
        }

        if (initialTranslations) {
            setTranslations(initialTranslations);
            setIsLoading(false);
        } else {
            const local = getLocalBundle(lang);
            setTranslations(local);
            setIsLoading(true);
        }

        setError(null);

        const now = Date.now();
        const isExpired =
            !stored || now - stored.fetchedAt > TRANSLATION_TTL_MS;

        if (!isExpired) {
            return;
        }

        getTranslations(apiLang)
            .then((data) => {
                // Merge backend translations over the local bundle
                const local = getLocalBundle(lang);
                const merged = { ...local, ...data };

                setTranslations(merged);

                const toStore: StoredTranslations = {
                    data: merged,
                    fetchedAt: Date.now(),
                };

                window.localStorage.setItem(storageKey, JSON.stringify(toStore));
                setIsLoading(false);
            })
            .catch((err) => {
                console.error("Failed to load translations:", err);

                if (!stored) {
                    setError("Could not load translations");
                    setIsLoading(false);
                }
            });
    }, [lang]);

    useEffect(() => {
        (window as any).__APP_LANG__ = lang;
    }, [lang]);

    const t = (key: string): string => {
        return translations[key] ?? key;
    };

    return (
        <TranslationContext.Provider
            value={{ lang, setLang: updateLang, translations, t, isLoading, error }}
        >
            {children}
        </TranslationContext.Provider>
    );
}

export function useTranslationContext() {
    return useContext(TranslationContext);
}
